import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  proposalTemplateDtoSchema,
  type ProposalTemplateDto,
  type ProposalTemplateOverrides,
} from '@lib/dto/proposal-template.dto'
import {
  proposalDocumentDtoSchema,
  type ProposalDocumentDto,
} from '@lib/dto/proposal-document.dto'
import type { ProposalWork } from '@lib/enums/proposal-work.enum'
import type { IProposalTemplateService } from '@/types/ProposalTemplateService.types'

/** The business's wording for one kind of proposal. */
function organizationPath(work: ProposalWork, suffix = ''): string {
  return `/proposal-template${suffix}?work=${encodeURIComponent(work)}`
}

/**
 * The wording proposals are built from.
 *
 * Two levels, same shape: the business's standard wording, and changes for one
 * proposal. The business keeps a set for each kind of proposal (trees,
 * irrigation, both); one proposal's changes sit over the set for what it
 * bids. Preview posts the wording rather than saving it first, so an edit can
 * be seen before it is committed to.
 */
class ProposalTemplateService implements IProposalTemplateService {
  async get(work: ProposalWork): Promise<ProposalTemplateDto> {
    try {
      return await api.get(organizationPath(work), proposalTemplateDtoSchema)
    } catch (error) {
      logger.error('Failed to load the proposal wording', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async save(
    work: ProposalWork,
    overrides: ProposalTemplateOverrides,
  ): Promise<ProposalTemplateDto> {
    try {
      return await api.put(
        organizationPath(work),
        overrides,
        proposalTemplateDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to save the proposal wording', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async preview(
    work: ProposalWork,
    overrides: ProposalTemplateOverrides,
  ): Promise<ProposalDocumentDto> {
    try {
      return await api.post(
        organizationPath(work, '/preview'),
        overrides,
        proposalDocumentDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to preview the proposal wording', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getForProposal(proposalId: string): Promise<ProposalTemplateDto> {
    try {
      return await api.get(
        `/proposals/${encodeURIComponent(proposalId)}/template`,
        proposalTemplateDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to load a proposal's wording", {
        proposalId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async saveForProposal(
    proposalId: string,
    overrides: ProposalTemplateOverrides,
  ): Promise<ProposalTemplateDto> {
    try {
      return await api.put(
        `/proposals/${encodeURIComponent(proposalId)}/template`,
        overrides,
        proposalTemplateDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to save a proposal's wording", {
        proposalId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async previewForProposal(
    proposalId: string,
    overrides: ProposalTemplateOverrides,
  ): Promise<ProposalDocumentDto> {
    try {
      return await api.post(
        `/proposals/${encodeURIComponent(proposalId)}/template/preview`,
        overrides,
        proposalDocumentDtoSchema,
      )
    } catch (error) {
      logger.error("Failed to preview a proposal's wording", {
        proposalId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const proposalTemplateService = new ProposalTemplateService()
